/**
 * Converts legacy graphics format to new format
 * Legacy format uses tildes (~) as field separators and stores data in:
 * - model.graphics.canvasData (array of strings)
 * - model.graphics.objectDefaults (string)
 * - model.graphics.viewSettings (string)
 * - model.graphics.openMonitors (string)
 */

import GraphUtils from '../../GraphUtils';

const FIELD_SEPARATOR = '~';
const STRING_SEPARATOR = ',';

/**
 * Parses a map string in the format "key1=value1,key2=value2"
 */
function parseMapString(mapString) {
  const map = {};
  if (!mapString || mapString === '$0$') {
    return map;
  }
  const pairs = mapString.split(STRING_SEPARATOR);
  pairs.forEach((pair) => {
    const eqIndex = pair.indexOf('=');
    if (eqIndex > 0) {
      const key = pair.substring(0, eqIndex);
      const value = pair.substring(eqIndex + 1);
      map[key] = value;
    }
  });
  return map;
}

/**
 * Parses polygon path data to extract bounding box center
 * Format: "0=x,y~1=x,y~3=x1,y1,x2,y2,x,y~4="
 * Type 0 (SEG_MOVETO): 2 coords (x, y)
 * Type 1 (SEG_LINETO): 2 coords (x, y)
 * Type 3 (SEG_CUBICTO): 6 coords (x1, y1, x2, y2, x, y)
 * Type 4 (SEG_CLOSE): 0 coords
 */
function parsePolygonPath(pathString) {
  if (!pathString || pathString === '$0$' || pathString.length === 0) {
    return null;
  }

  const segments = pathString.split('~');
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  segments.forEach((segment) => {
    if (!segment || segment.length <= 1) {
      return;
    }
    const eqIndex = segment.indexOf('=');
    if (eqIndex < 0) {
      return;
    }

    const typeStr = segment.substring(0, eqIndex);
    const type = parseInt(typeStr, 10);
    const coordsString = segment.substring(eqIndex + 1);
    const coords = coordsString.split(',').map((c) => parseFloat(c.trim())).filter((c) => !Number.isNaN(c));

    // Extract x,y coordinates based on segment type
    if (type === 0) {
      // SEG_MOVETO: 2 coords (x, y)
      if (coords.length >= 2) {
        minX = Math.min(minX, coords[0]);
        maxX = Math.max(maxX, coords[0]);
        minY = Math.min(minY, coords[1]);
        maxY = Math.max(maxY, coords[1]);
      }
    } else if (type === 1) {
      // SEG_LINETO: 2 coords (x, y)
      if (coords.length >= 2) {
        minX = Math.min(minX, coords[0]);
        maxX = Math.max(maxX, coords[0]);
        minY = Math.min(minY, coords[1]);
        maxY = Math.max(maxY, coords[1]);
      }
    } else if (type === 3) {
      // SEG_CUBICTO: 6 coords (x1, y1, x2, y2, x, y) - use the final point (x, y)
      if (coords.length >= 6) {
        minX = Math.min(minX, coords[4]);
        maxX = Math.max(maxX, coords[4]);
        minY = Math.min(minY, coords[5]);
        maxY = Math.max(maxY, coords[5]);
        // Also include control points for more accurate bounding box
        minX = Math.min(minX, coords[0], coords[2]);
        maxX = Math.max(maxX, coords[0], coords[2]);
        minY = Math.min(minY, coords[1], coords[3]);
        maxY = Math.max(maxY, coords[1], coords[3]);
      }
    }
    // Type 4 (SEG_CLOSE) has no coordinates
  });

  if (minX === Infinity || maxX === -Infinity || minY === Infinity || maxY === -Infinity) {
    return null;
  }

  return {
    x: (minX + maxX) / 2,
    y: (minY + maxY) / 2,
    width: maxX - minX,
    height: maxY - minY,
  };
}

function parseCanvasObjectHeader(tokens) {
  let tokenIndex = 1;
  const nextToken = () => {
    const value = tokens[tokenIndex];
    tokenIndex += 1;
    return value;
  };
  const atomicCOStr = nextToken();
  let id;
  if (atomicCOStr === 'true' || atomicCOStr === 'false') {
    id = parseInt(nextToken(), 10);
  } else {
    id = parseInt(atomicCOStr, 10);
  }

  const parentId = parseInt(nextToken(), 10);
  const relativeId = parseInt(nextToken(), 10);
  const shortDesc = nextToken();
  const longDesc = nextToken();

  return {
    id,
    parentId,
    relativeId,
    shortDesc,
    longDesc,
    tokenIndex,
  };
}

function readMetadataToken(tokens, startIndex) {
  let metadataStr = tokens[startIndex] || '';
  let metadataIndex = startIndex;
  for (let i = 0; i < 3 && startIndex + i < tokens.length; i += 1) {
    const candidate = tokens[startIndex + i];
    if (candidate && candidate.includes('uk.co.agena.minerva')) {
      metadataStr = candidate;
      metadataIndex = startIndex + i;
      break;
    }
  }
  return {
    metadata: parseMapString(metadataStr),
    nextIndex: metadataIndex + 1,
  };
}

function parseCanvasObjectBounds(tokens) {
  const header = parseCanvasObjectHeader(tokens);
  let { tokenIndex } = header;

  tokenIndex += 12;
  if (tokenIndex >= tokens.length) {
    return {
      id: header.id,
      parentId: header.parentId,
      metadata: {},
      position: null,
    };
  }

  const { metadata, nextIndex } = readMetadataToken(tokens, tokenIndex);
  tokenIndex = nextIndex;

  const polygonPathParts = [];
  while (tokenIndex < tokens.length) {
    const token = tokens[tokenIndex];
    if (!token || token.length <= 1 || !token.match(/^\d+=/)) {
      break;
    }
    polygonPathParts.push(token);
    tokenIndex += 1;
  }

  const polygonPath = polygonPathParts.join('~');
  const position = parsePolygonPath(polygonPath);

  return {
    id: header.id,
    parentId: header.parentId,
    metadata,
    position,
  };
}

function parseCanvasObjectGrouping(tokens) {
  const header = parseCanvasObjectHeader(tokens);
  let { tokenIndex } = header;
  tokenIndex += 12;
  const { metadata } = readMetadataToken(tokens, tokenIndex);

  return {
    id: header.id,
    parentId: header.parentId,
    metadata,
  };
}

function isNumericLine(value) {
  if (!value) {
    return false;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return false;
  }
  return !Number.isNaN(Number(trimmed));
}

/**
 * Parses a CanvassLabel line from canvasData
 * Format: className~atomicCO~id~parentId~relativeId~shortDesc~longDesc~borderColorR~borderColorG~borderColorB~lineThickness~transparancy~lineStyle~visibleBorder~filled~fillColorR~fillColorG~fillColorB~metadata~polygonPath~labelText~fontSize~fontName~fontStyle~fontColorR~fontColorG~fontColorB~clipText~buffer~hJust~vJust
 */
function parseCanvassLabel(line, tokens) {
  let tokenIndex = 0;

  // Skip class name (already parsed)
  tokenIndex++;

  // Parse atomicCO (boolean or id)
  const atomicCOStr = tokens[tokenIndex++];
  let id;
  // Check if it's a boolean string ("true" or "false")
  if (atomicCOStr === 'true' || atomicCOStr === 'false') {
    // It's a boolean, next token is id
    id = parseInt(tokens[tokenIndex++], 10);
  } else {
    // It's the id directly
    id = parseInt(atomicCOStr, 10);
  }

  const parentId = parseInt(tokens[tokenIndex++], 10);
  const relativeId = parseInt(tokens[tokenIndex++], 10);
  const shortDesc = tokens[tokenIndex++];
  const longDesc = tokens[tokenIndex++];

  // Debug: verify we're at the right position before reading border colors
  const beforeBorderColorIndex = tokenIndex;

  // Border style (readGridLine reads: color, lineThickness, transparancy, lineStyle)
  const borderColorRIndex = tokenIndex;
  const borderColorR = parseInt(tokens[tokenIndex++], 10);
  const borderColorGIndex = tokenIndex;
  const borderColorG = parseInt(tokens[tokenIndex++], 10);
  const borderColorBIndex = tokenIndex;
  const borderColorB = parseInt(tokens[tokenIndex++], 10);
  const lineThicknessIndex = tokenIndex;
  const lineThickness = parseFloat(tokens[tokenIndex++]);
  const transparancyIndex = tokenIndex;
  const transparancy = parseFloat(tokens[tokenIndex++]);
  const lineStyleIndex = tokenIndex;
  const lineStyleToken = tokens[lineStyleIndex];
  const lineStyle = parseInt(tokens[tokenIndex++], 10);


  // readCanvasObject then reads: transparancy (again), visibleBorder, filled, fillColour
  const transparancy2Index = tokenIndex;
  const transparancy2 = parseFloat(tokens[tokenIndex++]);
  const visibleBorderIndex = tokenIndex;
  const visibleBorderToken = tokens[tokenIndex];
  const visibleBorder = tokens[tokenIndex++] === '1';

  const filledIndex = tokenIndex;
  const filledToken = tokens[tokenIndex];
  const filled = tokens[tokenIndex++] === '1';

  // Read fillColor - these should be at indices filledIndex+1, filledIndex+2, filledIndex+3
  const fillColorRIndex = tokenIndex;
  const fillColorRToken = tokens[fillColorRIndex];
  const fillColorR = parseInt(fillColorRToken, 10);
  tokenIndex++; // Manually increment to ensure we're on the right token

  const fillColorGIndex = tokenIndex;
  const fillColorGToken = tokens[fillColorGIndex];
  const fillColorG = parseInt(fillColorGToken, 10);
  tokenIndex++; // Manually increment

  const fillColorBIndex = tokenIndex;
  const fillColorBToken = tokens[fillColorBIndex];
  const fillColorB = parseInt(fillColorBToken, 10);
  tokenIndex++; // Manually increment


  // Metadata (contains node connection info) - comes after fillColor
  // The metadata string contains "uk.co.agena.minerva.model.extendedbn.ExtendedBN=0,uk.co.agena.minerva.model.extendedbn.LabelledEN=7"
  // Looking at the raw tokens, metadata is at index 19
  // After reading fillColorB, tokenIndex is 18, so metadata is at tokenIndex + 1 (token 19)
  // But we need to check - sometimes there might be an extra field
  // Look ahead to find the token that contains "uk.co.agena.minerva.model.extendedbn"
  let metadataStr = '';
  let foundMetadata = false;
  for (let i = 0; i < 3 && tokenIndex + i < tokens.length; i += 1) {
    const candidate = tokens[tokenIndex + i];
    if (candidate && candidate.includes('uk.co.agena.minerva.model.extendedbn')) {
      metadataStr = candidate;
      tokenIndex += i + 1;
      foundMetadata = true;
      break;
    }
  }
  if (!foundMetadata) {
    // Fallback: just read the next token
    metadataStr = tokens[tokenIndex] || '';
    tokenIndex += 1;
  }
  const metadata = parseMapString(metadataStr);

  // Polygon path (for position) - spans multiple tokens until we find an empty one
  const polygonPathParts = [];
  const pathStartIndex = tokenIndex;
  while (tokenIndex < tokens.length) {
    const token = tokens[tokenIndex];
    // Empty token or token with length <= 1 indicates end of path
    if (!token || token.length <= 1 || token === '') {
      break;
    }
    // Check if this looks like the start of label text (not a path segment)
    // Path segments start with numbers like "0=" or "3="
    if (!token.match(/^\d+=/)) {
      break;
    }
    polygonPathParts.push(token);
    tokenIndex += 1;
  }
  const polygonPath = polygonPathParts.join('~');
  const position = parsePolygonPath(polygonPath);

  // Skip empty token(s) after polygon path
  while (tokenIndex < tokens.length && (!tokens[tokenIndex] || tokens[tokenIndex].length <= 1)) {
    tokenIndex += 1;
  }

  // Label-specific fields
  const labelText = (tokenIndex < tokens.length) ? tokens[tokenIndex] : '';
  tokenIndex += 1;
  const fontSize = (tokenIndex < tokens.length) ? parseInt(tokens[tokenIndex], 10) : 0;
  tokenIndex += 1;
  const fontName = (tokenIndex < tokens.length) ? tokens[tokenIndex] : '';
  tokenIndex += 1;
  const fontStyle = (tokenIndex < tokens.length) ? parseInt(tokens[tokenIndex], 10) : 0;
  tokenIndex += 1;
  const fontColorR = (tokenIndex < tokens.length) ? parseInt(tokens[tokenIndex], 10) : 0;
  tokenIndex += 1;
  const fontColorG = (tokenIndex < tokens.length) ? parseInt(tokens[tokenIndex], 10) : 0;
  tokenIndex += 1;
  const fontColorB = (tokenIndex < tokens.length) ? parseInt(tokens[tokenIndex], 10) : 0;
  tokenIndex += 1;
  const clipText = (tokenIndex < tokens.length) ? tokens[tokenIndex] === '1' : false;
  tokenIndex += 1;
  const buffer = (tokenIndex < tokens.length) ? parseInt(tokens[tokenIndex], 10) : 0;
  tokenIndex += 1;
  const hJust = (tokenIndex < tokens.length) ? parseInt(tokens[tokenIndex], 10) : 0;
  tokenIndex += 1;
  const vJust = (tokenIndex < tokens.length) ? parseInt(tokens[tokenIndex], 10) : 0;
  tokenIndex += 1;

  return {
    id,
    parentId,
    relativeId,
    metadata,
    position,
    labelText,
    fontSize,
    fontName,
    fontStyle,
    fontColor: { r: fontColorR, g: fontColorG, b: fontColorB },
    borderColor: { r: borderColorR, g: borderColorG, b: borderColorB },
    fillColor: { r: fillColorR, g: fillColorG, b: fillColorB },
    lineThickness,
    transparancy: transparancy2, // Use the second transparancy value (from readCanvasObject)
    lineStyle,
    visibleBorder,
    filled,
  };
}

/**
 * Converts line style integer to CSS border style
 * 0 = solid, 1 = dashed, 2 = dotted, etc.
 */
function lineStyleToCSS(lineStyle) {
  switch (lineStyle) {
    case 1:
      return 'dashed';
    case 2:
      return 'dotted';
    default:
      return 'solid';
  }
}

/**
 * Converts RGB color object to CSS color string
 */
function rgbToCSS(rgb) {
  return `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
}

/**
 * Converts transparency (0-1) to opacity
 */
function transparencyToOpacity(transparancy) {
  return transparancy;
}

/**
 * Finds the ExtendedNode index from metadata
 * Returns the node index (0-based) in the network's nodes array
 * Metadata format: "uk.co.agena.minerva.model.extendedbn.ExtendedBN=0,uk.co.agena.minerva.model.extendedbn.LabelledEN=7"
 * ExtendedBN=0 means first network (index 0)
 * LabelledEN=7 means 8th node (index 7) in that network
 */
function findNodeIdFromMetadata(metadata) {
  // Look for LabelledEN, ContinuousIntervalEN, DiscreteRealEN, RankedEN, etc.
  const enKeys = [
    'uk.co.agena.minerva.model.extendedbn.LabelledEN',
    'uk.co.agena.minerva.model.extendedbn.ContinuousIntervalEN',
    'uk.co.agena.minerva.model.extendedbn.DiscreteRealEN',
    'uk.co.agena.minerva.model.extendedbn.IntegerIntervalEN',
    'uk.co.agena.minerva.model.extendedbn.RankedEN',
  ];

  for (const key of enKeys) {
    if (metadata[key] !== undefined) {
      const nodeIndex = parseInt(metadata[key], 10);
      return nodeIndex;
    }
  }

  // Fallback: look for any key ending with EN (but not ExtendedBN)
  // This catches any ExtendedNode type that wasn't in the explicit list above
  // Check if it's from the extendedbn package (contains 'uk.co.agena.minerva.model.extendedbn')
  const keys = Object.keys(metadata);
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i];
    if (key.endsWith('EN') && key !== 'ExtendedBN' && !key.includes('ExtendedBN') && key.includes('uk.co.agena.minerva.model.extendedbn')) {
      const nodeIndex = parseInt(metadata[key], 10);
      return nodeIndex;
    }
  }
  return null;
}

/**
 * Finds the ExtendedBN index from metadata
 * Returns the network index (0-based) in the networks array
 * Metadata format: "uk.co.agena.minerva.model.extendedbn.ExtendedBN=0"
 * ExtendedBN=0 means first network (index 0)
 */
function findNetworkIdFromMetadata(metadata) {
  if (metadata['uk.co.agena.minerva.model.extendedbn.ExtendedBN'] !== undefined) {
    const networkIndex = parseInt(metadata['uk.co.agena.minerva.model.extendedbn.ExtendedBN'], 10);
    return networkIndex;
  }
  return null;
}

export function parseModelViewBoundsFromBlob(blob) {
  if (!blob) {
    return [];
  }

  const lines = blob.split(/\r?\n/);
  if (lines.length === 0) {
    return [];
  }

  let lineIndex = 0;
  if (isNumericLine(lines[0])) {
    lineIndex = 1;
  }

  if (lineIndex < lines.length && lines[lineIndex].includes('uk.co.agena.minerva.model.metadata.MetaDataItem')) {
    lineIndex += 1;
  }

  const groupings = new Map();
  const boundsByParentId = new Map();

  for (let i = lineIndex; i < lines.length; i += 1) {
    const line = lines[i].trim();
    if (!line || !line.startsWith('uk.co.agena.')) {
      continue;
    }

    const tokens = line.split(FIELD_SEPARATOR);
    if (tokens.length === 0) {
      continue;
    }

    const className = tokens[0];

    if (className.includes('CanvassObjectGrouping')) {
      const grouping = parseCanvasObjectGrouping(tokens);
      const networkIndex = findNetworkIdFromMetadata(grouping.metadata);
      if (networkIndex !== null) {
        groupings.set(grouping.id, { extendedBnId: networkIndex });
      }
      continue;
    }

    if (className.includes('CanvassPolygon') || className.includes('CanvassLabel')) {
      const canvasObject = parseCanvasObjectBounds(tokens);
      if (!canvasObject.position) {
        continue;
      }

      const isNodeBounds = findNodeIdFromMetadata(canvasObject.metadata) !== null;
      if (isNodeBounds) {
        continue;
      }

      const area = canvasObject.position.width * canvasObject.position.height;
      if (!Number.isFinite(area) || area <= 0) {
        continue;
      }

      if (!boundsByParentId.has(canvasObject.parentId)) {
        boundsByParentId.set(canvasObject.parentId, []);
      }
      boundsByParentId.get(canvasObject.parentId).push({
        ...canvasObject.position,
        area,
      });
    }
  }

  const results = [];
  groupings.forEach((grouping, groupingId) => {
    const boundsList = boundsByParentId.get(groupingId);
    if (!boundsList || boundsList.length === 0) {
      return;
    }
    const largest = boundsList.reduce((best, current) => (
      current.area > best.area ? current : best
    ));
    results.push({
      extendedBnId: grouping.extendedBnId,
      x: largest.x,
      y: largest.y,
      width: largest.width,
      height: largest.height,
    });
  });

  return results;
}

function parseModelViewBounds(canvasDataBlobs) {
  const bounds = [];
  if (!Array.isArray(canvasDataBlobs)) {
    return bounds;
  }

  canvasDataBlobs.forEach((blob) => {
    if (!blob) {
      return;
    }
    if (!blob.includes('uk.co.agena.minerva.model.metadata.MetaDataItem')) {
      return;
    }
    bounds.push(...parseModelViewBoundsFromBlob(blob));
  });

  return bounds;
}

/**
 * Parses openMonitors string to determine which nodes have charts open
 * Format: "ebnId,nodeId,location\n..."
 */
function parseOpenMonitors(openMonitorsStr, networks) {
  const openChartNodes = {}; // networkId -> Set of nodeIds

  if (!openMonitorsStr) {
    return openChartNodes;
  }

  const networkIdMap = {};
  networks.forEach((net, index) => {
    networkIdMap[index] = net.id;
  });

  const lines = openMonitorsStr.split(/\r?\n/);
  lines.forEach((line) => {
    const parts = line.split(',');
    if (parts.length >= 2) {
      const ebnId = parseInt(parts[0], 10);
      const nodeIndex = parseInt(parts[1], 10);
      const networkId = networkIdMap[ebnId];

      if (networkId && networks[ebnId] && networks[ebnId].nodes) {
        // Validate node index is within bounds
        const nodeCount = networks[ebnId].nodes.length;
        if (nodeIndex >= 0 && nodeIndex < nodeCount) {
          const node = networks[ebnId].nodes[nodeIndex];
          if (node && node.id) {
            const nodeId = node.id;
            if (!openChartNodes[networkId]) {
              openChartNodes[networkId] = new Set();
            }
            openChartNodes[networkId].add(nodeId);
          }
        }
      }
    }
  });

  return openChartNodes;
}

/**
 * Parses canvasData blob to extract node graphics
 */
function parseCanvasData(canvasDataBlobs, networks) {
  const nodeGraphicsMap = {}; // networkId -> nodeId -> graphics

  // Create network ID map
  const networkIdMap = {};
  networks.forEach((net, index) => {
    networkIdMap[index] = net.id;
  });

  canvasDataBlobs.forEach((blob) => {
    if (!blob) {
      return;
    }

    const lines = blob.split('\n');
    if (lines.length === 0) {
      return;
    }

    // First line might be version number
    let lineIndex = 0;
    try {
      parseFloat(lines[0]);
      lineIndex = 1; // Skip version
    } catch (e) {
      // Not a version number, start from 0
    }

    // Second line is DiagramCanvas metadata
    if (lineIndex < lines.length) {
      const dcMetadata = parseMapString(lines[lineIndex]);
      lineIndex++;

      // Find which network this canvas belongs to
      const ebnId = dcMetadata['uk.co.agena.minerva.model.extendedbn.ExtendedBN'];
      const networkId = ebnId !== undefined ? networkIdMap[parseInt(ebnId, 10)] : null;

      if (!networkId) {
        return; // Skip if we can't identify the network
      }

      if (!nodeGraphicsMap[networkId]) {
        nodeGraphicsMap[networkId] = {};
      }

      // Process remaining lines
      for (let i = lineIndex; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line || !line.startsWith('uk.co.agena.')) {
          continue;
        }

        const tokens = line.split(FIELD_SEPARATOR);
        if (tokens.length === 0) {
          continue;
        }

        const className = tokens[0];

        if (className.includes('CanvassLabel')) {
          try {
            const labelData = parseCanvassLabel(line, tokens);
            const nodeIndex = findNodeIdFromMetadata(labelData.metadata);

            if (nodeIndex !== null && labelData.position) {
              // Find the network by index (ExtendedBN=0 means first network, etc.)
              // First, find which network index this canvas belongs to
              const ebnId = findNetworkIdFromMetadata(labelData.metadata);
              let targetNetwork = null;
              if (ebnId !== null && ebnId >= 0 && ebnId < networks.length) {
                targetNetwork = networks[ebnId];
              } else {
                // Fallback: find by networkId
                targetNetwork = networks.find((n) => n.id === networkId);
              }

              if (targetNetwork && targetNetwork.nodes && targetNetwork.nodes[nodeIndex] && nodeIndex >= 0) {
                const actualNodeId = targetNetwork.nodes[nodeIndex].id;

                const targetNetworkId = targetNetwork.id;
                if (!nodeGraphicsMap[targetNetworkId]) {
                  nodeGraphicsMap[targetNetworkId] = {};
                }
                if (!nodeGraphicsMap[targetNetworkId][actualNodeId]) {
                  nodeGraphicsMap[targetNetworkId][actualNodeId] = {
                    position: {},
                    style: {},
                    classes: [],
                  };
                }

                // Set position - use the escaped target network ID (as required by GraphUtils.escapeSelector)
                const escapedNetworkId = GraphUtils.escapeSelector(targetNetworkId);
                // Extract position (x, y) and size (width, height) from the parsed polygon
                const {
                  x, y, width, height,
                } = labelData.position || {};
                nodeGraphicsMap[targetNetworkId][actualNodeId].position[escapedNetworkId] = { x, y };

                // Build style object
                const style = {};

                // Apply width and height if available
                if (width !== undefined && !Number.isNaN(width) && width > 0) {
                  style.width = `${width}px`;
                }
                if (height !== undefined && !Number.isNaN(height) && height > 0) {
                  style.height = `${height}px`;
                }

                // Text color - only apply if valid RGB values
                // Check if we have a background color first to determine if white text should be skipped
                const hasNonWhiteBackground = labelData.filled && labelData.fillColor
                  && typeof labelData.fillColor.r === 'number' && !Number.isNaN(labelData.fillColor.r)
                  && typeof labelData.fillColor.g === 'number' && !Number.isNaN(labelData.fillColor.g)
                  && typeof labelData.fillColor.b === 'number' && !Number.isNaN(labelData.fillColor.b)
                  && !(labelData.fillColor.r === 255 && labelData.fillColor.g === 255 && labelData.fillColor.b === 255);

                if (labelData.fontColor
                  && typeof labelData.fontColor.r === 'number' && !Number.isNaN(labelData.fontColor.r)
                  && typeof labelData.fontColor.g === 'number' && !Number.isNaN(labelData.fontColor.g)
                  && typeof labelData.fontColor.b === 'number' && !Number.isNaN(labelData.fontColor.b)) {
                  // Check if color is white (255, 255, 255) - only skip if background is also white or missing
                  const isWhite = labelData.fontColor.r === 255 && labelData.fontColor.g === 255 && labelData.fontColor.b === 255;
                  if (!isWhite || hasNonWhiteBackground) {
                    style.color = rgbToCSS(labelData.fontColor);
                  }
                }

                // Font size - only apply if valid positive number
                if (labelData.fontSize && typeof labelData.fontSize === 'number' && !Number.isNaN(labelData.fontSize) && labelData.fontSize > 0) {
                  style['font-size'] = `${labelData.fontSize}px`;
                }

                // Border/line styles (shape line)
                // Always set border-style to override any default dashed styles
                // Default to solid unless lineStyle is explicitly 2 (dotted)
                // Note: In legacy format, lineStyle=1 may not actually mean dashed, so we default to solid
                if (labelData.lineStyle === 2) {
                  // Explicitly dotted
                  style['border-style'] = 'dotted';
                } else {
                  // Default to solid for all other cases (including lineStyle=0, 1, or undefined)
                  style['border-style'] = 'solid';
                }

                // Only apply other border styles if there's actually a visible border with thickness > 0
                if (labelData.visibleBorder && labelData.lineThickness !== undefined && labelData.lineThickness > 0) {
                  if (labelData.borderColor && (labelData.borderColor.r !== undefined || labelData.borderColor.g !== undefined || labelData.borderColor.b !== undefined)) {
                    style['border-color'] = rgbToCSS(labelData.borderColor);
                  }
                  style['border-width'] = `${labelData.lineThickness}px`;

                  // Opacity/transparency for border
                  if (labelData.transparancy !== undefined && labelData.transparancy < 1.0) {
                    style['border-opacity'] = transparencyToOpacity(labelData.transparancy);
                  }
                }

                // Background/fill color - only apply if filled is true and valid RGB values
                if (labelData.filled && labelData.fillColor
                  && typeof labelData.fillColor.r === 'number' && !Number.isNaN(labelData.fillColor.r)
                  && typeof labelData.fillColor.g === 'number' && !Number.isNaN(labelData.fillColor.g)
                  && typeof labelData.fillColor.b === 'number' && !Number.isNaN(labelData.fillColor.b)) {
                  // Skip if color is white (255, 255, 255) as that's likely the default
                  const isWhite = labelData.fillColor.r === 255 && labelData.fillColor.g === 255 && labelData.fillColor.b === 255;
                  if (!isWhite) {
                    const cssColor = rgbToCSS(labelData.fillColor);
                    style['background-color'] = cssColor;
                    // Apply transparency/opacity to background if less than 1.0
                    if (labelData.transparancy !== undefined && labelData.transparancy < 1.0) {
                      style['background-opacity'] = transparencyToOpacity(labelData.transparancy);
                    }
                  }
                }

                // Merge styles (don't overwrite existing)
                Object.assign(nodeGraphicsMap[targetNetworkId][actualNodeId].style, style);
              }
            }
          } catch (error) {
            // eslint-disable-next-line no-console
            console.warn('[GraphicsConverter] Error parsing CanvassLabel:', error, line);
          }
        }
      }
    }
  });

  return nodeGraphicsMap;
}

/**
 * Main conversion function
 * Converts legacy graphics format to new format
 */
export function convertLegacyGraphics(model) {
  if (!model || !model.graphics) {
    return model;
  }

  const { graphics } = model;

  // Check if already in new format
  if (graphics.graph) {
    return model; // Already converted
  }

  // Check if we have legacy data
  if (!graphics.canvasData || !Array.isArray(graphics.canvasData) || graphics.canvasData.length === 0) {
    return model; // No legacy data to convert
  }

  // Parse canvasData to extract node graphics
  const nodeGraphicsMap = parseCanvasData(graphics.canvasData, model.networks || []);
  const modelViewBounds = parseModelViewBounds(graphics.canvasData);

  // Parse openMonitors to determine which nodes have charts open
  const openChartNodes = parseOpenMonitors(graphics.openMonitors, model.networks || []);

  // Apply graphics to nodes
  let appliedCount = 0;
  if (model.networks) {
    model.networks.forEach((network) => {
      if (network.nodes && nodeGraphicsMap[network.id]) {
        network.nodes.forEach((node) => {
          const legacyGraphics = nodeGraphicsMap[network.id][node.id];
          if (legacyGraphics) {
            if (!node.graphics) {
              node.graphics = {};
            }
            if (!node.graphics.graph) {
              node.graphics.graph = {
                position: {},
                style: {
                  custom: {},
                },
                classes: [],
              };
            }

            // Merge position
            // Ensure position exists (assume empty object if it doesn't)
            if (!node.graphics.graph.position) {
              node.graphics.graph.position = {};
            }
            Object.assign(node.graphics.graph.position, legacyGraphics.position);

            // Merge style (base style)
            // Ensure style exists (assume empty object if it doesn't)
            if (!node.graphics.graph.style) {
              node.graphics.graph.style = {};
            }
            Object.assign(node.graphics.graph.style, legacyGraphics.style);

            // Ensure classes exists (assume empty array if it doesn't)
            if (!node.graphics.graph.classes) {
              node.graphics.graph.classes = [];
            }

            // If node has chart open, add mode_chart class
            if (openChartNodes[network.id] && openChartNodes[network.id].has(node.id)) {
              if (!node.graphics.graph.classes.includes('mode_chart')) {
                node.graphics.graph.classes.push('mode_chart');
              }
            }

            // Merge classes
            if (legacyGraphics.classes) {
              legacyGraphics.classes.forEach((cls) => {
                if (!node.graphics.graph.classes.includes(cls)) {
                  node.graphics.graph.classes.push(cls);
                }
              });
            }

            appliedCount++;
          }
        });
      }
    });
  }

  let appliedNetworkBounds = 0;
  if (model.networks && modelViewBounds.length > 0) {
    modelViewBounds.forEach((bounds) => {
      const network = model.networks[bounds.extendedBnId];
      if (!network) {
        return;
      }
      if (!network.graphics) {
        network.graphics = {};
      }
      if (!network.graphics.graph) {
        network.graphics.graph = {};
      }
      if (!network.graphics.graph.position) {
        network.graphics.graph.position = {};
      }
      if (!network.graphics.graph.size) {
        network.graphics.graph.size = {};
      }
      if (Number.isFinite(bounds.x) && Number.isFinite(bounds.y)) {
        network.graphics.graph.position = { x: bounds.x, y: bounds.y };
      }
      if (Number.isFinite(bounds.width) && Number.isFinite(bounds.height)) {
        network.graphics.graph.size = { width: bounds.width, height: bounds.height };
      }
      appliedNetworkBounds += 1;
    });
  }

  // Mark that this model was converted from legacy format only if we actually applied graphics
  // This flag helps distinguish freshly converted models from pre-existing ones
  if (appliedCount > 0 || appliedNetworkBounds > 0) {
    graphics.convertedFromLegacy = true;
  }

  // Set network-level graphics to use "preset" layout for networks that have node positions
  // This tells Cytoscape to use the preset positions instead of calculating a new layout
  if (appliedCount > 0 && model.networks) {
    const networksWithPositions = new Set();
    model.networks.forEach((network) => {
      if (network.nodes) {
        const hasNodePositions = network.nodes.some((node) => {
          const nodeGraphics = node.graphics?.graph?.position;
          if (!nodeGraphics) return false;
          // Check if any graphKey has a position set
          return Object.keys(nodeGraphics).some((graphKey) => {
            const pos = nodeGraphics[graphKey];
            return pos && typeof pos.x === 'number' && typeof pos.y === 'number';
          });
        });
        if (hasNodePositions) {
          networksWithPositions.add(network.id);
        }
      }
    });

    // Set layout to "preset" for networks with positions
    networksWithPositions.forEach((networkId) => {
      const network = model.networks.find((n) => n.id === networkId);
      if (network) {
        if (!network.graphics) {
          network.graphics = {};
        }
        if (!network.graphics.cyto) {
          network.graphics.cyto = {};
        }
        network.graphics.cyto.layout = 'preset';
      }
    });
  }

  // Create graph-level graphics structure
  if (!graphics.graph) {
    graphics.graph = {
      position: {},
      style: {
        custom: {},
      },
      classes: [],
    };
  }

  // Note: We don't remove canvasData after conversion, so its presence
  // can be used to distinguish freshly converted models from pre-existing ones

  // Note: openMonitors, objectDefaults, and viewSettings are not converted
  // as they are not part of the node-level graphics format

  return model;
}
